/**
 * Popup display-condition evaluation.
 *
 * Pure functions, no I/O — the widget runs them on every page load and the
 * API runs them when validating a saved config, so there is exactly one
 * definition of "may this popup show here".
 *
 * Deliberately excluded: frequency. Deciding `daily` / `hourly` needs
 * persisted per-visitor state, which lives in localStorage on the widget
 * side (see `apps/widget/src/popup-frequency.ts`).
 */

import type {
  PopupConditions,
  PopupDevice,
  PopupSchedule,
  PopupSource,
  PopupTargets,
} from './popup-types'

/** Everything a popup can be gated on, gathered once from the page. */
export interface PopupContext {
  /** Full page URL. */
  url: string
  /** `location.pathname` — extracted from `url` when omitted. */
  path?: string
  /** `location.hostname` — extracted from `url` when omitted. */
  hostname?: string
  referrer?: string
  device?: PopupDevice | string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  /** Injected so tests (and schedule checks) are deterministic. */
  now?: Date
}

/** Hosts that make a referrer "social" rather than plain "referring". */
const SOCIAL_HOSTS = [
  'facebook.com',
  'fb.com',
  'm.facebook.com',
  'instagram.com',
  'zalo.me',
  'linkedin.com',
  'tiktok.com',
  'youtube.com',
  'twitter.com',
  'x.com',
  'threads.net',
]

/** Search engines whose organic SERP arrivals count as "organic". */
const ORGANIC_HOSTS = ['google.', 'bing.com', 'duckduckgo.com', 'yahoo.', 'baidu.com', 'yandex.']

function hostOf(url: string | undefined): string {
  if (!url) return ''
  try {
    return new URL(url).hostname.toLowerCase()
  } catch {
    return ''
  }
}

function pathOf(url: string | undefined): string {
  if (!url) return '/'
  try {
    const parsed = new URL(url, 'https://placeholder.invalid')
    return parsed.pathname || '/'
  } catch {
    return '/'
  }
}

function hostnameOf(url: string | undefined): string {
  if (!url) return ''
  try {
    // A relative URL has no hostname; only an absolute one does.
    return new URL(url).hostname.toLowerCase()
  } catch {
    return ''
  }
}

function searchParamOf(url: string | undefined, key: string): string {
  if (!url) return ''
  try {
    return new URL(url, 'https://placeholder.invalid').searchParams.get(key)?.trim() ?? ''
  } catch {
    return ''
  }
}

function normalizeDevice(device: PopupContext['device']): PopupDevice | null {
  if (!device) return null
  const value = device.toLowerCase()
  if (value === 'mobile' || value === 'tablet' || value === 'desktop') return value
  // Widgets report these spellings from some hosts.
  if (value.includes('tablet')) return 'tablet'
  if (value.includes('mobi') || value.includes('phone')) return 'mobile'
  return 'desktop'
}

function includesValue<T>(list: T[] | undefined, value: T | undefined): boolean {
  // An empty (or absent) filter list means "no constraint on this axis".
  if (!list || list.length === 0) return true
  if (value === undefined || value === null) return false
  const wanted = String(value).toLowerCase()
  return list.some((item) => String(item).toLowerCase() === wanted)
}

/**
 * Classify the visit into a traffic bucket.
 *
 * The UTM check comes first on purpose. A click from a paid ad very often
 * carries no referrer at all (the ad platform strips it), which is exactly
 * how paid traffic ends up mislabelled as "direct" — an advertiser then
 * sees zero traffic and a zero conversion rate for a campaign that
 * delivered leads. Any `utm_source` or `utm_medium` is therefore treated
 * as advertising regardless of referrer.
 */
export function classifySource(ctx: PopupContext): PopupSource {
  // Fall back to the URL's own query string so a caller holding only a URL
  // still gets the right bucket — the widget passes parsed UTM fields, but
  // the API and tests may only have the link.
  const utmSource = ctx.utmSource?.trim() || searchParamOf(ctx.url, 'utm_source')
  const utmMedium = ctx.utmMedium?.trim() || searchParamOf(ctx.url, 'utm_medium')
  if (utmSource || utmMedium) return 'advertising'

  const referrerHost = hostOf(ctx.referrer)
  const pageHost = ctx.hostname ?? hostnameOf(ctx.url)
  if (!referrerHost) return 'direct'
  // Same-site navigation is not a referral.
  if (pageHost && (referrerHost === pageHost || referrerHost.endsWith(`.${pageHost}`))) {
    return 'direct'
  }
  if (SOCIAL_HOSTS.some((host) => referrerHost === host || referrerHost.endsWith(`.${host}`))) {
    return 'social'
  }
  if (ORGANIC_HOSTS.some((host) => referrerHost.includes(host))) return 'organic'
  return 'referring'
}

function domainMatches(hostname: string, pattern: string): boolean {
  const value = pattern.trim().toLowerCase()
  if (!value) return false
  if (value === hostname) return true
  if (value.startsWith('*.')) {
    const suffix = value.slice(1) // ".example.com"
    return hostname.endsWith(suffix)
  }
  // Bare "example.com" also covers its subdomains — that is what an owner
  // typing a domain into the field means.
  return hostname.endsWith(`.${value}`)
}

function matchesTargets(targets: PopupTargets | undefined, ctx: PopupContext): string | null {
  if (!targets) return null

  const hostname = (ctx.hostname ?? hostnameOf(ctx.url)).toLowerCase()
  if (targets.domains?.length) {
    if (!hostname || !targets.domains.some((pattern) => domainMatches(hostname, pattern))) {
      return 'domain'
    }
  }

  const path = ctx.path ?? pathOf(ctx.url)
  // Exclude is evaluated first and is absolute — it beats any include.
  if (targets.pathExclude?.some((fragment) => fragment && path.includes(fragment))) {
    return 'path-exclude'
  }
  if (targets.pathInclude?.length) {
    if (!targets.pathInclude.some((fragment) => fragment && path.includes(fragment))) {
      return 'path-include'
    }
  }

  const device = normalizeDevice(ctx.device)
  if (targets.devices?.length && !includesValue(targets.devices, device)) return 'device'

  if (targets.sources?.length && !includesValue(targets.sources, classifySource(ctx))) {
    return 'source'
  }
  if (
    targets.utmSource?.length &&
    !includesValue(targets.utmSource, ctx.utmSource?.trim())
  ) {
    return 'utm-source'
  }
  if (
    targets.utmMedium?.length &&
    !includesValue(targets.utmMedium, ctx.utmMedium?.trim())
  ) {
    return 'utm-medium'
  }
  if (
    targets.utmCampaign?.length &&
    !includesValue(targets.utmCampaign, ctx.utmCampaign?.trim())
  ) {
    return 'utm-campaign'
  }
  if (targets.referrerKeywords?.length) {
    const referrer = (ctx.referrer ?? '').toLowerCase()
    const hit = targets.referrerKeywords.some(
      (keyword) => keyword.trim() && referrer.includes(keyword.trim().toLowerCase()),
    )
    if (!hit) return 'referrer-keyword'
  }

  return null
}

function parseHhMm(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

function insideSchedule(schedule: PopupSchedule | undefined, now: Date): string | null {
  if (!schedule) return null
  const windows = schedule.windows ?? []
  // Days alone with no windows means "only on these days, all day".
  if (windows.length === 0) {
    return schedule.days?.length && !schedule.days.includes(now.getDay()) ? 'schedule-day' : null
  }

  const day = now.getDay()
  const minutes = now.getHours() * 60 + now.getMinutes()
  const dayAllowed = !schedule.days?.length || schedule.days.includes(day)
  if (!dayAllowed) return 'schedule-day'

  for (const window of windows) {
    const from = parseHhMm(window.from)
    const to = parseHhMm(window.to)
    if (from === null || to === null) continue
    // A window that ends at or before it starts wraps past midnight
    // (22:00 → 02:00 covers the small hours of the following day too).
    const inside = to > from ? minutes >= from && minutes < to : minutes >= from || minutes < to
    if (inside) return null
  }
  return 'schedule-window'
}

/**
 * Decide whether a popup's targeting rules let it render on this page.
 *
 * `reason` names the first axis that blocked it, so the dashboard can
 * explain "why isn't my popup showing?" instead of silently showing
 * nothing. The trigger itself (delay / scroll / exit-intent) is handled
 * by the widget — it needs timers and listeners, which this function
 * deliberately does not own.
 */
export function evaluatePopupConditions(
  conditions: PopupConditions | undefined,
  ctx: PopupContext,
): { show: boolean; reason: string } {
  if (!conditions) return { show: true, reason: 'no-conditions' }

  const blockedBySchedule = insideSchedule(conditions.schedule, ctx.now ?? new Date())
  if (blockedBySchedule) return { show: false, reason: blockedBySchedule }

  const blockedByTargets = matchesTargets(conditions.targets, ctx)
  if (blockedByTargets) return { show: false, reason: blockedByTargets }

  return { show: true, reason: 'ok' }
}

/**
 * Effective delay in ms for the time-based triggers. `immediate` is 0 and
 * `scroll` / `exit_intent` have no timer at all (null).
 */
export function triggerDelayMs(conditions: PopupConditions | undefined): number | null {
  const trigger = conditions?.trigger ?? 'immediate'
  if (trigger === 'immediate') return 0
  if (trigger === 'delay') return Math.max(0, (conditions?.delaySec ?? 0) * 1000)
  if (trigger === 'idle') return Math.max(0, (conditions?.idleSec ?? 0) * 1000)
  return null
}
